/**
 * `<caos-mention-picker>` — the shortlist that opens while somebody types a mention. (Catalog: Conversation › MentionPicker — the shortlist of people and records offered while somebody types an @mention.)
 *
 * A list of candidates and nothing else: it does not fetch them, does not decide what matches, and
 * does not know what a person or a record is. The surface fetches (people from the `mentions`
 * noun, records from `search global`), hands them here, and acts on the choice.
 *
 * ── IT NEVER TAKES FOCUS, AND THAT IS THE WHOLE DESIGN ────────────────────────────────────────
 * Somebody typing a mention is typing INTO THE COMPOSER. If this stole focus to be arrow-navigable,
 * the next character they typed would go nowhere — which is the failure that makes a mention picker
 * worse than no mention picker. So it is a listbox the composer OWNS: the composer keeps focus,
 * forwards the arrow keys through {@link CaosMentionPicker.moveDown} and its neighbours, and points
 * at the active row with `aria-activedescendant`. That is the combobox pattern, and it is the only
 * arrangement in which typing and choosing can both work.
 *
 * The consequence worth stating: this element has no keyboard handler of its own. If it is on
 * screen and nothing is forwarding keys to it, it is a list that can only be clicked — which is
 * correct, not broken.
 *
 * ── WHAT IT REFUSES TO DO ─────────────────────────────────────────────────────────────────────
 * It does not filter. The candidates it is given are the candidates it draws, in the order it is
 * given them. A picker that filtered again would be a second opinion about what matches, disagreeing
 * with the server's the moment either changed — and the server's is the one that applied the access
 * rules.
 *
 * Styling seams: `::part(list)`, `::part(option)`, `::part(label)`, `::part(hint)`, `::part(empty)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { applyAccessibleName } from '../accessible-name.js';
import type { MentionKind } from '../mention-token.js';

/** One thing that can be mentioned. */
export interface CaosMentionCandidate {
  kind: MentionKind;
  /** The reference a mention stores — a principal id, or `<object>/<id>`. */
  ref: string;
  /** What it is called. Drawn, and kept in the mention as the label. */
  label: string;
  /** A second line telling two identical labels apart — an address, or the object's name. */
  hint?: string;
}

const sheet = makeSheet(`
  :host { display: block; }
  :host([hidden]) { display: none !important; }

  .list {
    margin: 0;
    padding: var(--caos-space-1);
    list-style: none;
    max-height: 220px;
    overflow-y: auto;
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    box-shadow: var(--caos-shadow-float);
    font-family: var(--caos-font);
    font-size: var(--caos-text-row);
  }

  .option {
    display: flex;
    align-items: baseline;
    gap: var(--caos-space-2);
    padding: 5px var(--caos-space-2);
    border-radius: var(--caos-radius-sm);
    color: var(--caos-color-text);
    cursor: pointer;
  }
  /* The active row is marked by an ATTRIBUTE, not by :hover or :focus — the pointer is not where
     the person is, and focus is in the composer. */
  .option[data-active="true"] {
    background: var(--caos-color-accent-fill);
    color: var(--caos-color-accent-contrast);
  }

  .label { flex: 0 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .hint {
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--caos-color-text-subtle);
    font-size: var(--caos-text-label);
  }
  .option[data-active="true"] .hint { color: var(--caos-color-accent-contrast); }

  .empty {
    padding: var(--caos-space-2);
    color: var(--caos-color-text-muted);
    font-family: var(--caos-font);
    font-size: var(--caos-text-row);
  }
`);

export class CaosMentionPicker extends CaosElement {
  static get observedAttributes(): string[] {
    return ['empty-label', 'aria-label'];
  }

  private readonly list: HTMLUListElement;
  private _candidates: CaosMentionCandidate[] = [];
  private _activeIndex = 0;

  constructor() {
    super(sheet);
    this.list = document.createElement('ul');
    this.list.className = 'list';
    this.list.setAttribute('part', 'list');
    // A listbox the composer points into. Its options carry ids so `aria-activedescendant` on the
    // composer's textarea can name one without focus ever leaving the textarea.
    this.list.setAttribute('role', 'listbox');
    this.root.append(this.list);
  }

  connectedCallback(): void {
    this.syncName();
    this.render();
  }

  attributeChangedCallback(name: string): void {
    if (name === 'aria-label') {
      this.syncName();
      return;
    }
    this.render();
  }

  private syncName(): void {
    applyAccessibleName(this.list, this.getAttribute('aria-label'), 'Mention suggestions');
  }

  /** The candidates, in the order they should read. Setting them puts the active row back at the top. */
  get candidates(): CaosMentionCandidate[] {
    return this._candidates;
  }
  set candidates(list: readonly CaosMentionCandidate[]) {
    this._candidates = [...list];
    // A new shortlist means a new first choice. Keeping the old index would leave the highlight on
    // whoever happens to be in that position now, and Enter would choose them.
    this._activeIndex = 0;
    this.render();
  }

  /** Which row Enter would choose. Always in range; 0 when there is nothing. */
  get activeIndex(): number {
    return this._activeIndex;
  }

  /** The candidate Enter would choose, or null when there is nothing to choose. */
  get active(): CaosMentionCandidate | null {
    return this._candidates[this._activeIndex] ?? null;
  }

  /** The id of the active option, for the composer's `aria-activedescendant`. */
  get activeOptionId(): string | null {
    return this.active ? this.optionId(this._activeIndex) : null;
  }

  /** Move the highlight down, wrapping. A no-op with nothing to move through. */
  moveDown(): void {
    this.move(1);
  }

  /** Move the highlight up, wrapping. */
  moveUp(): void {
    this.move(-1);
  }

  private move(step: number): void {
    if (this._candidates.length === 0) return;
    // Wrapping, because a shortlist is short: reaching the bottom and stopping makes somebody
    // travel back up through a list they can see all of.
    const n = this._candidates.length;
    this._activeIndex = (this._activeIndex + step + n) % n;
    this.reflectActive();
  }

  /**
   * Choose the highlighted candidate. Returns what was chosen, or null when there was nothing —
   * so a composer can tell "Enter chose somebody" from "Enter should send the message".
   */
  chooseActive(): CaosMentionCandidate | null {
    const choice = this.active;
    if (!choice) return null;
    this.dispatchEvent(new CustomEvent('choose', { bubbles: true, composed: true, detail: { ...choice } }));
    return choice;
  }

  private optionId(index: number): string {
    return `caos-mention-option-${index}`;
  }

  private render(): void {
    this.list.replaceChildren();
    if (this._candidates.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'empty';
      empty.setAttribute('part', 'empty');
      // `presentation`, not an option: it is not choosable, and announcing it as one row of a
      // listbox tells a reader there is something to pick when there is not.
      empty.setAttribute('role', 'presentation');
      empty.textContent = this.getAttribute('empty-label') ?? 'No matches.';
      this.list.append(empty);
      return;
    }

    this._candidates.forEach((candidate, index) => {
      const option = document.createElement('li');
      option.className = 'option';
      option.setAttribute('part', 'option');
      option.setAttribute('role', 'option');
      option.id = this.optionId(index);
      option.dataset['ref'] = candidate.ref;
      option.dataset['kind'] = candidate.kind;

      const label = document.createElement('span');
      label.className = 'label';
      label.setAttribute('part', 'label');
      label.textContent = candidate.label;
      option.append(label);

      if (candidate.hint) {
        const hint = document.createElement('span');
        hint.className = 'hint';
        hint.setAttribute('part', 'hint');
        hint.textContent = candidate.hint;
        option.append(hint);
      }

      // `mousedown`, not `click`: a click on a list beside a focused textarea blurs the textarea
      // first, and a composer that loses focus mid-mention loses the caret the insertion needs.
      // Prevented default keeps focus where the person put it.
      option.addEventListener('mousedown', (event) => {
        event.preventDefault();
        this._activeIndex = index;
        this.reflectActive();
        this.chooseActive();
      });

      this.list.append(option);
    });

    this.reflectActive();
  }

  private reflectActive(): void {
    const options = Array.from(this.list.querySelectorAll<HTMLElement>('li.option'));
    options.forEach((option, index) => {
      const active = index === this._activeIndex;
      option.dataset['active'] = String(active);
      option.setAttribute('aria-selected', String(active));
    });
    this.dispatchEvent(new CustomEvent('active-change', { bubbles: true, composed: true, detail: { index: this._activeIndex, id: this.activeOptionId } }));
  }
}

define('caos-mention-picker', CaosMentionPicker);

declare global {
  interface HTMLElementTagNameMap {
    'caos-mention-picker': CaosMentionPicker;
  }
}
